/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { ITasksConfig, INotesConfig, IQueuesConfig } from 'app/core/models/essette-interfaces';
import { RouteConfig, NavigationInstruction } from 'aurelia-router';
import { ITaskBinding } from 'app/core/components/tasks/task';
import { apiCall } from 'app/shared/lib/api/api';
import { TaskService } from 'app/core/services/task-service';
import { IApiQueryResults } from 'app/shared/lib/api/api-types';
import { safe } from 'app/shared/lib/utilities';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { I18N } from 'aurelia-i18n';
import * as CM from 'app/cm/models/essette-api';


@autoinject()
export class TaskDetailsPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected taskId: string | null = null;
    protected case: CM.Case | null = null;
    protected taskBinding: ITaskBinding | null = null;

    @bindable.string({ changeHandler: 'titleReload' })
    protected taskDisplay: string | null = null;

    protected routeConfig: RouteConfig | null = null;

    protected tasksConfig: ITasksConfig = {
        primaryTargetType: 'CM.CaseTask',
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
        backRouteName: 'cm.case.tasks',
        addTargetProperty: this.addCaseProperty.bind(this),

        smartTasksConfig: {
            moduleTaskCode: 'CM.Task',
            assessmentsConfig: {
                serviceRootUrl: 'cm/cases',
                targetType: 'CM.Case',
                backRouteName: 'cm.case.assessments'
            },
            documentsConfig: {
                serviceRootUrl: 'cm/cases',
                targetType: 'CM.Case',
                addTargetProperty: (document: CM.CaseDocument) => { document.case = { id: safe(() => this.case!.id) }; }
            },
            notesConfig: {
                serviceRootUrl: 'cm/cases',
                targetType: 'CM.Case',
                addTargetProperty: (note: CM.CaseNote) => { note.case = { id: safe(() => this.case!.id) }; }
            },
            correspondenceConfig: {
                transitionalPageId: '3051',
                moduleCode: 'CM',
                serviceRootUrl: 'cm/cases',
                rollupRootUrl: 'cm/cases/rollup/correspondence'
            },
            contactLogConfig: {
                serviceRootUrl: 'cm/cases',
                targetType: 'CM.Case',
                rollupConfig: {
                    serviceRootUrl: 'cd/members',
                    targetType: 'CD.Member'
                }
            }
        },

        appointmentsConfig: {
            addTargetProperty: (appointment: CM.CaseTaskAppointment) => { appointment.task = { id: this.taskId }; }
        }
    };

    private notesConfig: INotesConfig = {
        serviceRootUrl: 'cm/cases/tasks',
        targetType: 'CM.CaseTask',
        addTargetProperty: this.addTaskProperty.bind(this),
        excludeSystem: false
    };

    private queuesConfig: IQueuesConfig = {
        serviceRootUrl: 'cm/tasks'
    }

    ////****************** Element References ***************************************////



    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: TaskService,
        private readonly context: CasePageContext,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected async activate(params: { taskId: string }, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<void> {
        if(!params.taskId) { throw new Error('Missing required parameter [taskId]'); }

        this.taskId = params.taskId;
        this.tasksConfig.memberId = this.context.case?.member?.id || undefined;

        this.service.configure(this.tasksConfig);

        await this.loadTask(this.taskId);
        if(!this.case || !this.case.id) { throw new Error('Case was not loaded'); }
        this.context.case = this.case;
        if(!this.case.member || !this.case.member.id) { throw new Error('Could not load member id'); }

        await this.accountContext.load();
        const canEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');

        this.taskBinding = {
            targetId: this.case.id,
            taskId: this.taskId,
            smartTaskTargetId: this.case.id,
            tasksConfig: this.tasksConfig,
            notesConfig: this.notesConfig,
            queuesConfig: this.queuesConfig,
            readonly: this.context.caseIsReadOnly(canEditClosedCase),
            relatedModels: {
                "CD.Member": this.case.member.id,
                "CM.Case": this.case.id
            },
            canDeactivateNotes: true,
            showAppointments: true,
            showTaskQueues: true,
            canUpdateTaskDueDate: true,
            showDelegateToQueue: true,
            showDelegateToUser: true,
            canSaveTask: true
        };

        this.routeConfig = routeConfig;
    }


    ////****************** Events ***************************************************////
    protected titleReload(): void {
        if(this.routeConfig?.navModel) {
            this.routeConfig.navModel.setTitle(this.i18n.tr('cm:routes.tasks.$title', { what: this.taskDisplay }));
        }
    }

    ////****************** Functions ************************************************////
    public addCaseProperty(task: CM.CaseTask): void {
        if(!(this.case && this.case.id)) {
            throw new Error('Cannot add case propery: Missing case object');
        }

        task.case = { id: this.case.id };
    }

    public addTaskProperty(model: CM.CaseTaskNote | CM.CaseTaskAppointment): void {
        model.task = { id: this.taskId };
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadTask(taskId: string): Promise<void> {
        if(!taskId) { return; }
        const includes: string[] = ['case'];
        try {
            this.state.beginLoad();
            const response: IApiQueryResults<CM.CaseTask> = await this.service.loadTask(taskId, includes);
            this.case = response.results[0].case || null;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
